import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { NavigationStart, Router } from "@angular/router";
import { Observable, Subject } from "rxjs";

export type AlertType =
  | "SUCCESS"
  | "WARNING"
  | "ERROR"
  | "BAD_REQUEST"
  | "SERVER_ERROR"
  | "ACCOUNT_SUSPENDED"
  | "CONNECTION_PROBLEM";

export const AlertType = {
  SUCCESS: "SUCCESS" as AlertType,
  WARNING: "WARNING" as AlertType,
  ERROR: "ERROR" as AlertType,
  BAD_REQUEST: "BAD_REQUEST" as AlertType,
  SERVER_ERROR: "SERVER_ERROR" as AlertType,
  ACCOUNT_SUSPENDED: "ACCOUNT_SUSPENDED" as AlertType,
  CONNECTION_PROBLEM: "CONNECTION_PROBLEM" as AlertType,
  PASSWORD_LOST_SUCCESS: "PASSWORD_LOST_SUCCESS" as AlertType,
};

export class AlertMessage {
  text?: string;
  type: AlertType;
  apiError?: HttpErrorResponse;
}

@Injectable({ providedIn: "root" })
export class AlertService {
  private subject = new Subject<AlertMessage>();
  private keepAfterRouteChange = false;

  constructor(private router: Router, private dialogRef: MatDialog) {
    // clear alert messages on route change unless 'keepAfterRouteChange' flag is true
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (this.keepAfterRouteChange) {
          // only keep for a single route change
          this.keepAfterRouteChange = false;
        } else {
          // clear alert message
          this.clear();
        }
      }
    });
  }

  getAlert(): Observable<AlertMessage> {
    return this.subject.asObservable();
  }

  success(message: string, keepAfterRouteChange = false) {
    this.keepAfterRouteChange = keepAfterRouteChange;
    this.subject.next({ type: AlertType.SUCCESS, text: message });
  }

  warning(message: string, keepAfterRouteChange = false) {
    this.keepAfterRouteChange = keepAfterRouteChange;
    this.subject.next({ type: AlertType.WARNING, text: message });
  }

  error(message: string, keepAfterRouteChange = false) {
    this.keepAfterRouteChange = keepAfterRouteChange;
    this.subject.next({ type: AlertType.ERROR, text: message });
  }

  badRequest(message: string, keepAfterRouteChange = false) {
    this.keepAfterRouteChange = keepAfterRouteChange;
    this.subject.next({ type: AlertType.BAD_REQUEST, text: message });
  }

  apiBadRequest(
    error: HttpErrorResponse,
    message?: string,
    keepAfterRouteChange = false
  ) {
    this.keepAfterRouteChange = keepAfterRouteChange;
    this.subject.next({
      type: AlertType.BAD_REQUEST,
      apiError: error,
      text: message,
    });
    // on api error, close all dialogs (otherwise the message is behind the dialog)
    this.dialogRef.closeAll();
  }

  apiServerError(
    error: HttpErrorResponse,
    message?: string,
    keepAfterRouteChange = false
  ) {
    this.keepAfterRouteChange = keepAfterRouteChange;
    this.subject.next({
      type: AlertType.SERVER_ERROR,
      apiError: error,
      text: message,
    });
    // on api error, close all dialogs (otherwise the message is behind the dialog)
    this.dialogRef.closeAll();
  }

  passwordLostSuccess() {
    this.keepAfterRouteChange = false;
    this.subject.next({ type: AlertType.PASSWORD_LOST_SUCCESS });
  }

  accountSuspendedError() {
    this.keepAfterRouteChange = false;
    this.subject.next({ type: AlertType.ACCOUNT_SUSPENDED });
  }

  connectionProblemError() {
    this.keepAfterRouteChange = false;
    this.subject.next({ type: AlertType.CONNECTION_PROBLEM });
  }

  clear() {
    this.subject.next(null);
  }
}
